import { Info, Lock, Quote } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FeatureOff } from "../FeatureOff";
import { Pill } from "../Pill";
import { RoleButton, RoleGroup } from "../RequireRole";
import { SourceLink } from "../SourceLink";
import { TableScroll } from "../TableScroll";
import { Empty, ErrorState } from "../States";
import { apiSend } from "../../lib/http";
import type { Me } from "../../lib/me";
import {
  advanceBlock, caseFailure, MOVE_LABELS, nextState, OUTCOMES, outcomeLabel, OUTCOME_LABELS, resolveBlock, stateLabel,
  type Case, type CaseState, type Outcome,
} from "./caseFlow";
import { StatePill, shortDate } from "./CaseList";
import { assetPath } from "./exports";

export function recordResolution(id: string, outcome: Outcome, note: string): Promise<Case> {
  return apiSend("/api/v1/cases/{case_id}/resolutions", { outcome, note: note.trim() || null }, { path: { case_id: id } });
}

export function moveCase(id: string, state: CaseState): Promise<Case> {
  return apiSend("/api/v1/cases/{case_id}/state", { state }, { path: { case_id: id } });
}


function ResolveForm({ c, me, busy, onResolve }: {
  c: Case; me: Me | null | undefined; busy: boolean; onResolve: (o: Outcome, note: string) => void;
}) {
  const [outcome, setOutcome] = useState<Outcome>(c.resolutions.length ? "new_item_or_source" : "approved_item");
  const [note, setNote] = useState("");
  const block = resolveBlock(c);
  const locked = !!block;
  return (
    <div className="card" style={{ padding: 16 }}>
      <h3 style={{ fontSize: 16, marginBottom: 10 }}>{c.resolutions.length ? "Record a later resolution" : "Record a resolution"}</h3>
      <RoleGroup role="engineer" me={me} label="Record a resolution">
      <div className="stack" style={{ gap: 10 }}>
        <label className="stack" style={{ gap: 4 }}>
          <span className="muted" style={{ fontSize: 13 }}>Resolution (SD-22)</span>
          <select value={outcome} disabled={locked} onChange={(e) => setOutcome(e.target.value as Outcome)}>
            {OUTCOMES.map((o) => <option key={o} value={o}>{OUTCOME_LABELS[o]}</option>)}
          </select>
        </label>
        <label className="stack" style={{ gap: 4 }}>
          <span className="muted" style={{ fontSize: 13 }}>Note (optional)</span>
          <input value={note} disabled={locked} onChange={(e) => setNote(e.target.value)}
                 placeholder="Part number, quantity, who agreed it" />
        </label>
        <div>
          {/* The group above carries the role's reason; this button adds only the case's own. */}
          <RoleButton role="viewer" me={me} block={block}
                      disabled={busy} onClick={() => onResolve(outcome, note)}>Record resolution</RoleButton>
        </div>
      </div>
      </RoleGroup>
    </div>
  );
}

/** The move that ends a case asks first: a closed case takes no more resolutions or moves. */
export function confirmsFirst(next: CaseState): boolean {
  return next === "closed";
}

/** The question before a case is closed. Focus starts on the safe answer. */
export function CloseConfirm({ tag, busy, onConfirm, onCancel }: {
  tag: string; busy: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  const id = useId();
  const keep = useRef<HTMLButtonElement>(null);
  useEffect(() => { keep.current?.focus(); }, []);
  return (
    <div role="alertdialog" aria-labelledby={`${id}-q`} aria-describedby={`${id}-d`} className="card"
         style={{ padding: 16, borderColor: "var(--color-fail)" }}
         onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}>
      <p id={`${id}-q`}><strong>Close the case for {tag}?</strong></p>
      <p id={`${id}-d`} className="muted" style={{ fontSize: 13, margin: "6px 0 12px" }}>
        A closed case takes no more resolutions and cannot be moved again. Its history is kept.
      </p>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <button onClick={onConfirm} disabled={busy}>{busy ? "Closing..." : "Close the case"}</button>
        <button ref={keep} className="ghost" onClick={onCancel} disabled={busy}>Keep it open</button>
      </div>
    </div>
  );
}

export function CaseBody({ c, me, busy, problem, onResolve, onMove }: {
  c: Case; me: Me | null | undefined; busy: boolean; problem: string | null;
  onResolve: (outcome: Outcome, note: string) => void; onMove: (to: CaseState) => void;
}) {
  const next = nextState(c.state);
  const [confirming, setConfirming] = useState(false);
  const source = c.source as { source_url?: string | null; quote?: string | null; authority?: string | null };
  return (
    <>
      <div className="row" style={{ gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <StatePill state={c.state} />
        <span>Tag <Link className="mono" to={assetPath(c.package, c.tag)}>{c.tag}</Link></span>
        <span className="muted">{c.package} package</span>
        <span className="muted">Register version {c.register_entry.version}</span>
        <span className="muted">Opened {shortDate(c.created_at)} by {c.opened_by}</span>
      </div>

      {problem && (
        <div className="state-box fail" role="alert" style={{ maxWidth: "none", marginBottom: 16 }}>
          <Info aria-hidden="true" />
          <div><div className="state-title">The server refused this</div><p className="muted">{problem}</p></div>
        </div>
      )}

      <section className="card" style={{ padding: 16, marginBottom: 24 }}>
        <div className="row" style={{ gap: 8, marginBottom: 8 }}>
          <Quote size={16} aria-hidden="true" /> <strong>Finding</strong> <Pill value={c.lifecycle} />
          {source.authority && <span className="muted" style={{ fontSize: 13 }}>{source.authority.replace(/_/g, " ")}</span>}
        </div>
        {source.quote ? <blockquote style={{ margin: "8px 0", fontStyle: "italic" }}>"{source.quote}"</blockquote>
          : <p className="muted">No quote recorded.</p>}
        <SourceLink url={source.source_url ?? ""} />
      </section>

      <div className="grid-2" style={{ gap: 24, marginBottom: 24 }}>
        <section>
          <h3 style={{ fontSize: 18, marginBottom: 12 }}>Resolutions</h3>
          {c.resolutions.length === 0 ? <p className="muted">None recorded yet.</p> : (
            <ul className="stub-list">
              {c.resolutions.map((r, i) => (
                <li key={i}>
                  <strong>{outcomeLabel(r.outcome)}</strong>
                  {typeof r.note === "string" && r.note && <span>: {r.note}</span>}
                  <div className="muted" style={{ fontSize: 13 }}>
                    {typeof r.by === "string" ? r.by : "unknown"}{typeof r.at === "string" ? `, ${shortDate(r.at)}` : ""}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div style={{ marginTop: 12 }}>
            {next && confirming && confirmsFirst(next) ? (
              <CloseConfirm tag={c.tag} busy={busy}
                            onConfirm={() => onMove(next)}
                            onCancel={() => setConfirming(false)} />
            ) : next ? (
              <RoleButton role="engineer" me={me} block={advanceBlock(c)} disabled={busy}
                          onClick={() => (confirmsFirst(next) ? setConfirming(true) : onMove(next))}>
                {MOVE_LABELS[next]}
              </RoleButton>
            ) : <p className="muted">The case is closed.</p>}
          </div>
        </section>
        <ResolveForm c={c} me={me} busy={busy} onResolve={onResolve} />
      </div>

      <h3 style={{ fontSize: 18, marginBottom: 12 }}>State history</h3>
      <TableScroll label="State history" className="card" style={{ padding: 0 }}>
        <table>
          <thead><tr><th>From</th><th>To</th><th>By</th><th>When</th></tr></thead>
          <tbody>
            {c.transitions.map((t, i) => (
              <tr key={i}>
                <td>{typeof t.from === "string" ? stateLabel(t.from) : <span className="muted">opened</span>}</td>
                <td>{stateLabel(String(t.to))}</td>
                <td>{String(t.by ?? "")}</td>
                <td className="mono">{typeof t.at === "string" ? shortDate(t.at) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

export function CaseDetailFailure({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const kind = caseFailure(error);
  const message = error instanceof Error ? error.message : String(error);
  if (kind === "off") return <FeatureOff feature="Cases" reason={message} />;
  if (kind === "missing") {
    return <Empty title="No case at this address">It may have been opened on another environment. <Link to="/cases">All cases</Link>.</Empty>;
  }
  if (kind === "forbidden") {
    return (
      <div className="state-box" role="status">
        <Lock aria-hidden="true" />
        <div><div className="state-title">This case is outside your access</div><p className="muted">{message}</p></div>
      </div>
    );
  }
  return <ErrorState error={error} onRetry={onRetry} />;
}
